import { create } from 'zustand';
import { track } from '@utils/analytics';

/**
 * Three-step wizard: Room → Objects → Analyze. Mirrors DESIGN.md §5
 * "Wizard sidebar" and the legacy `goToStep()` progression semantics
 * (can't skip forward, but can go backwards freely).
 */
export type WizardStep = 1 | 2 | 3;

/** Default sidebar width as a percentage of the viewport. 40/60 split. */
const DEFAULT_SIDEBAR_PCT = 40;
const MAX_STEP: WizardStep = 3;

interface UIState {
  currentStep: WizardStep;
  /** Highest step the user has reached — used to lock forward navigation. */
  highestStep: WizardStep;
  isSplashVisible: boolean;
  isDonationBannerVisible: boolean;
  /** Sidebar width as a percentage of viewport width (25–55%). */
  sidebarWidthPct: number;
  /** Active on-canvas hint id, or null for the default hint. */
  activeHint: string | null;
  /** True when the About modal is open. */
  isAboutVisible: boolean;
  /** Set once when the user first arrives on Step 3 — prevents the donation
   *  banner from re-triggering on subsequent visits. */
  _hasTriggeredDonation: boolean;

  // ── Analysis transition / wave playback ──────────────────────────────────
  /** True while the wave animation is running and the analysis panel is
   *  transitioning from Step 2 → Step 3. */
  isAnalysisTransition: boolean;
  /** True while the wave rays are actively animating in the 3D viewport. */
  isWaveActive: boolean;
  /** Overall progress of the transition, 0–1. */
  waveProgress: number;
  /** Human-readable label for the current phase of the analysis. */
  wavePhase: string;
  /** RT60 hero value revealed at ~82% progress; null before that point. */
  waveRevealRT60: number | null;

  goToStep: (step: WizardStep) => void;
  nextStep: () => void;
  prevStep: () => void;
  dismissSplash: () => void;
  showDonationBanner: () => void;
  dismissDonationBanner: () => void;
  setSidebarWidthPct: (pct: number) => void;
  setActiveHint: (hintId: string | null) => void;
  showAbout: () => void;
  dismissAbout: () => void;
  /** Called once when Step 3 is first reached — schedules the donation banner. */
  triggerDonationOnFirstAnalysis: () => void;

  /**
   * Kick off the multi-phase analysis transition.
   * @param rt60Hero  The computed RT60 value to reveal at ~82% through.
   * @param duration  Total duration of the wave animation in milliseconds.
   */
  startAnalysisTransition: (rt60Hero: number, duration: number) => void;
  /** Cancel all pending timeouts and reset wave/transition state. */
  resetAnalysisTransition: () => void;
}

// Internal store for pending timeout IDs so resetAnalysisTransition can clear
// them reliably.  These live outside Zustand state because timeout IDs are
// not serializable/observable and should never trigger re-renders.
const _pendingTimeouts: ReturnType<typeof setTimeout>[] = [];

function clearPendingTimeouts(): void {
  for (const id of _pendingTimeouts) clearTimeout(id);
  _pendingTimeouts.length = 0;
}

export const useUIStore = create<UIState>((set, get) => ({
  currentStep: 1,
  highestStep: 1,
  isSplashVisible: true,
  isDonationBannerVisible: false,
  sidebarWidthPct: DEFAULT_SIDEBAR_PCT,
  activeHint: null,
  isAboutVisible: false,
  _hasTriggeredDonation: false,

  // Analysis transition initial state
  isAnalysisTransition: false,
  isWaveActive: false,
  waveProgress: 0,
  wavePhase: '',
  waveRevealRT60: null,

  goToStep: (step) => {
    const from = get().currentStep;
    set((state) => {
      // Don't allow forward jumps past the furthest reached step.
      if (step > state.highestStep) return state;
      return { currentStep: step };
    });
    if (get().currentStep !== from) {
      track('step_advanced', { from, to: get().currentStep });
    }
  },

  nextStep: () => {
    const s = get().currentStep;
    if (s >= MAX_STEP) return;
    const next = (s + 1) as WizardStep;
    set((state) => ({
      currentStep: next,
      highestStep: (next > state.highestStep ? next : state.highestStep) as WizardStep,
    }));
    track('step_advanced', { from: s, to: next });
  },

  prevStep: () => {
    const s = get().currentStep;
    if (s <= 1) return;
    const prev = (s - 1) as WizardStep;
    set({ currentStep: prev });
    track('step_advanced', { from: s, to: prev });
  },

  dismissSplash: () => set({ isSplashVisible: false }),
  showDonationBanner: () => set({ isDonationBannerVisible: true }),
  dismissDonationBanner: () => set({ isDonationBannerVisible: false }),
  setSidebarWidthPct: (pct) => set({ sidebarWidthPct: Math.max(25, Math.min(55, pct)) }),
  setActiveHint: (hintId) => set({ activeHint: hintId }),
  showAbout: () => {
    set({ isAboutVisible: true });
    track('about_opened');
  },
  dismissAbout: () => set({ isAboutVisible: false }),
  triggerDonationOnFirstAnalysis: () => {
    const state = get();
    if (state._hasTriggeredDonation || state.isDonationBannerVisible) return;
    set({ _hasTriggeredDonation: true });
    _pendingTimeouts.push(
      setTimeout(() => {
        set({ isDonationBannerVisible: true });
        track('donation_shown');
      }, 2500),
    );
  },

  startAnalysisTransition: (rt60Hero, duration) => {
    // Cancel any in-flight transition first.
    clearPendingTimeouts();

    set({
      isAnalysisTransition: true,
      isWaveActive: true,
      waveProgress: 0,
      wavePhase: 'Tracing sound reflections…',
      waveRevealRT60: null,
    });
    track('analysis_started', { duration });

    // Phase schedule: [delay_ms, progress_0_to_1, label]
    const phases: Array<[number, number, string]> = [
      [300,                    0.15, 'Tracing sound reflections…'],
      [duration * 0.25,        0.30, 'Calculating wall bounces…'],
      [duration * 0.50,        0.55, 'Measuring absorption…'],
      [duration * 0.70,        0.75, 'Computing reverb decay…'],
      [duration * 0.85,        0.90, 'Finalizing analysis…'],
    ];

    for (const [delay, progress, label] of phases) {
      _pendingTimeouts.push(
        setTimeout(() => {
          set({ waveProgress: progress, wavePhase: label });
        }, delay),
      );
    }

    // At 82% of duration: reveal RT60 and complete the progress bar.
    _pendingTimeouts.push(
      setTimeout(() => {
        set({ waveProgress: 1, wavePhase: '', waveRevealRT60: rt60Hero });
      }, duration * 0.82),
    );

    // After animation + 1200ms cool-down: navigate to Step 3 and clean up.
    _pendingTimeouts.push(
      setTimeout(() => {
        // Advance highestStep so goToStep(3) is allowed.
        const { highestStep } = get();
        if ((3 as WizardStep) > highestStep) {
          set({ highestStep: 3 as WizardStep });
        }
        get().goToStep(3);
        set({ isAnalysisTransition: false, isWaveActive: false });
      }, duration + 1200),
    );
  },

  resetAnalysisTransition: () => {
    clearPendingTimeouts();
    set({
      isAnalysisTransition: false,
      isWaveActive: false,
      waveProgress: 0,
      wavePhase: '',
      waveRevealRT60: null,
    });
  },
}));
